export type PermissionRule = string | string[] | null | undefined;

export type RouteAccessRule = {
  /** Permission(s) required to open the page and show it in navigation */
  view: PermissionRule;
  /** Permission(s) required for create actions; hidden when user lacks these */
  create?: PermissionRule;
  /** Permission(s) required for edit actions */
  edit?: PermissionRule;
  /** Permission(s) required for delete actions */
  delete?: PermissionRule;
  /** Subscription feature code; nav/route blocked when feature disabled */
  feature?: string | null;
};

export type PageAccess = {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export function checkPermissionRule(
  rule: PermissionRule,
  has: (code: string) => boolean,
): boolean {
  if (rule === undefined || rule === null) return true;
  if (Array.isArray(rule)) return rule.some(has);
  return has(rule);
}

export function resolvePageAccess(
  rule: RouteAccessRule | undefined,
  has: (code: string) => boolean,
): PageAccess {
  if (!rule) {
    return { canView: true, canCreate: true, canEdit: true, canDelete: true };
  }
  const canView = checkPermissionRule(rule.view, has);
  const canCreate = canView && checkPermissionRule(rule.create ?? rule.view, has);
  const canEdit = canView && checkPermissionRule(rule.edit ?? rule.create ?? rule.view, has);
  const canDelete = canView && checkPermissionRule(rule.delete ?? rule.edit ?? rule.create ?? rule.view, has);
  return { canView, canCreate, canEdit, canDelete };
}

/** View with optional elevated manage/create/edit permissions */
export function access(
  view: PermissionRule,
  manage?: PermissionRule,
  feature?: string | null,
): RouteAccessRule {
  const elevated = manage ?? view;
  return {
    view: mergeViewRules(view, elevated),
    create: elevated,
    edit: elevated,
    delete: elevated,
    feature,
  };
}

function mergeViewRules(view: PermissionRule, manage: PermissionRule): PermissionRule {
  const codes = new Set<string>();
  const add = (rule: PermissionRule) => {
    if (!rule) return;
    if (Array.isArray(rule)) rule.forEach((c) => codes.add(c));
    else codes.add(rule);
  };
  add(view);
  add(manage);
  const list = [...codes];
  if (list.length <= 1) return list[0] ?? null;
  return list;
}

export function resolveRouteAccessRule(
  pathname: string,
  rules: Record<string, RouteAccessRule>,
): RouteAccessRule | undefined {
  const path = pathname.split('?')[0];
  if (rules[path]) return rules[path];
  const prefix = Object.keys(rules)
    .filter((p) => p !== '/pos' && path.startsWith(p + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? rules[prefix] : undefined;
}

export function resolveRouteViewPermission(
  pathname: string,
  rules: Record<string, RouteAccessRule>,
): PermissionRule | undefined {
  const rule = resolveRouteAccessRule(pathname, rules);
  return rule?.view;
}
